import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, CreditCard, ShieldCheck, AlertTriangle } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PublicInvoice() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);
  const [paid, setPaid] = useState(false);

  const loadInvoice = useCallback(() => {
    api
      .get(`/public/invoices/${token}`)
      .then((r) => {
        setInvoice(r.data);
        if (r.data.status === "paid") setPaid(true);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invoice not found"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Poll payment status after returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let active = true;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/public/invoices/${token}/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid" || data.invoice_status === "paid") {
          setPaid(true);
          setPolling(false);
          setParams({}, { replace: true });
          loadInvoice();
          return;
        }
      } catch (e) {
        /* keep trying */
      }
      if (attempts >= 15) {
        if (active) setPolling(false);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, token, loadInvoice, setParams]);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );

  if (error && !invoice)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9] p-6">
        <Card className="p-10 text-center max-w-md" data-testid="public-invoice-error">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" strokeWidth={1.3} />
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F8FAF9] py-10 px-4" data-testid="public-invoice-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <img src="/logo.png" alt="SmartHost Solutions" className="h-9 w-9 object-contain" />
          <span className="font-display text-xl font-bold">{invoice.company_name}</span>
        </div>

        <Card className="overflow-hidden">
          <div className="bg-primary text-primary-foreground px-6 py-5">
            <p className="text-xs uppercase tracking-[0.15em] opacity-80">Invoice</p>
            <h1 className="font-display text-2xl font-semibold">{invoice.number}</h1>
            {invoice.property_name && (
              <p className="text-sm opacity-90 mt-1">{invoice.property_name}</p>
            )}
          </div>

          <div className="p-6">
            {paid ? (
              <div
                className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center mb-5"
                data-testid="invoice-paid-banner"
              >
                <CheckCircle2 className="h-9 w-9 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-emerald-800">Payment received — thank you!</p>
                <p className="text-sm text-emerald-700 mt-1">
                  This invoice has been paid in full.
                </p>
              </div>
            ) : polling ? (
              <div className="rounded-xl bg-secondary p-5 text-center mb-5" data-testid="invoice-polling">
                <Loader2 className="h-7 w-7 animate-spin text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Confirming your payment…</p>
              </div>
            ) : null}

            {invoice.homeowner_name && (
              <p className="text-sm text-muted-foreground mb-4">
                Billed to <b className="text-foreground">{invoice.homeowner_name}</b>
              </p>
            )}

            <div className="space-y-2">
              {invoice.line_items.map((li, i) => (
                <div key={i} className="flex justify-between text-sm" data-testid={`invoice-line-${i}`}>
                  <span className="text-muted-foreground">{li.description}</span>
                  <span className="font-medium">{money(li.quantity * li.unit_price)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-border mt-4 pt-4 space-y-1.5">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Subtotal</span>
                <span>{money(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Tax</span>
                <span>{money(invoice.tax)}</span>
              </div>
              {invoice.late_fee > 0 && (
                <div className="flex justify-between text-sm text-[#B45309]">
                  <span>Late fee</span>
                  <span>{money(invoice.late_fee)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <span className="font-display text-lg font-semibold">Total</span>
                <span className="font-display text-2xl font-bold text-primary" data-testid="invoice-total">
                  {money(invoice.total)}
                </span>
              </div>
            </div>

            {!paid && (
              <Button
                className="w-full h-12 text-base mt-6"
                onClick={payNow}
                disabled={paying || polling}
                data-testid="pay-now-button"
              >
                {paying ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-5 w-5 mr-2" />
                )}
                Pay {money(invoice.total)} now
              </Button>
            )}

            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure payment powered by Stripe
            </p>
          </div>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Smart Systems. Seamless Stays.
        </p>
      </div>
    </div>
  );
}
